import React, { useEffect, useState, useMemo, useCallback } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchOrganizationAssessmentLinks } from "../../redux/actions/assessmentActions";
import { RootState, AppDispatch } from "../../redux/store";
import { format } from "date-fns";
import Fuse from "fuse.js";
import {
  getAgentConfigDisplayName,
  getAssessmentDisplayName,
  getRoleplayDisplayName,
} from "../../utils/render";

interface ResLinksTabProps {
  organizationId: string;
}

interface ResLinkRow {
  id: string;
  type: "assessment" | "roleplay";
  resource_id: string | null;
  resource_key: string | null;
  resource_name: string;
  agent_config_id: string | null;
  agent_config_name: string;
  display_name: string | null;
  created_at: string;
  mainLinkUrl: string;
  testLinkUrl: string;
}

const ResLinksTab: React.FC<ResLinksTabProps> = ({ organizationId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const { organizationAssessmentLinks, linksLoading } = useSelector(
    (state: RootState) => state.assessments
  );

  const { agentConfigs } = useSelector(
    (state: RootState) => state.agentConfigs
  );

  const { assessments } = useSelector((state: RootState) => state.assessments);

  const { roleplays } = useSelector((state: RootState) => state.roleplays);

  const { currentOrganization } = useSelector(
    (state: RootState) => state.organizations
  );

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganizationAssessmentLinks(organizationId));
    }
  }, [dispatch, organizationId]);

  const handleCopyLink = (linkId: string, url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  const handleViewResource = useCallback(
    (type: string, resourceId: string, orgId: string) => {
      if (type === "assessment") {
        navigate(`/dashboard/assessments/${orgId}/${resourceId}`);
      } else if (type === "roleplay") {
        // Extract ID from "roleplay:<id>" format if needed
        const id = resourceId.startsWith("roleplay:")
          ? resourceId.split(":")[1]
          : resourceId;
        navigate(`/dashboard/roleplays/${orgId}/${id}`);
      }
    },
    [navigate]
  );

  // Transform links into rows - handles both assessment and roleplay links
  const rows: ResLinkRow[] = useMemo(() => {
    if (!currentOrganization) return [];

    return organizationAssessmentLinks.map((link) => {
      const agentConfig = agentConfigs.find(
        (config) => config.id === link.agent_config_id
      );

      // Determine link type from backend behavior_type field
      // Map backend types to frontend types
      let linkType: "assessment" | "roleplay" = "assessment";
      let resourceId: string | null = null;
      let resourceKey: string | null = null;
      let resourceName = "N/A";

      if (link.behavior_type === "roleplay_demo") {
        linkType = "roleplay";
        resourceKey = link.roleplay_key || null;

        // Look up roleplay from roleplays state to get both ID and display name
        const roleplay = roleplays.find(
          (r) => r.resource_key === link.roleplay_key
        );

        // Use the roleplay's actual ID from the Redux state (not the key)
        resourceId = roleplay?.id || link.roleplay_id || null;
        
        // Generate display name using helper
        if (roleplay) {
          resourceName = getRoleplayDisplayName(roleplay);
        } else {
          resourceName = link.roleplay_key || link.roleplay_id || "N/A";
        }
      } else {
        // Default to assessment
        linkType = "assessment";
        resourceId = link.assessment_id || null;
        resourceKey = link.assessment_key || null;

        const assessment = assessments.find(
          (a) => a.id === link.assessment_id || a.resource_key === link.assessment_key
        );
        
        // Generate display name using helper
        if (assessment) {
          resourceName = getAssessmentDisplayName(assessment);
        } else {
          resourceName = link.assessment_id || link.assessment_key || "N/A";
        }
      }

      const mainLinkUrl = `https://pixels.persona-ai.ai/login?res_link_params_id=${link.id}&org_id=${currentOrganization.id}`;
      const testLinkUrl = `https://stagingpixels.persona-ai.ai/login?res_link_params_id=${link.id}&org_id=${currentOrganization.id}`;

      return {
        id: link.id,
        type: linkType,
        resource_id: resourceId,
        resource_key: resourceKey,
        resource_name: resourceName,
        agent_config_id: link.agent_config_id,
        agent_config_name: agentConfig && agentConfig.id
          ? getAgentConfigDisplayName({
              display_name: agentConfig.display_name,
              bot_name: agentConfig.bot_name,
              id: agentConfig.id
            })
          : link.agent_config_id || "N/A",
        display_name: link.display_name || null,
        created_at: link.created_at,
        mainLinkUrl,
        testLinkUrl,
      };
    });
  }, [
    organizationAssessmentLinks,
    agentConfigs,
    assessments,
    roleplays,
    currentOrganization,
  ]);

  // Apply filters
  const filteredRows = useMemo(() => {
    let data = rows;

    // Filter by type
    if (typeFilter) {
      data = data.filter((item) => item.type === typeFilter);
    }

    // Apply search query
    if (searchQuery) {
      const fuse = new Fuse(data, {
        keys: ["id", "display_name", "resource_name", "agent_config_name"],
        threshold: 0.3,
      });
      data = fuse.search(searchQuery).map((result) => result.item);
    }

    return data;
  }, [rows, searchQuery, typeFilter]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "display_name",
        headerName: "Link Name",
        flex: 2,
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ResLinkRow;
          const displayText = row.display_name || row.id;
          return (
            <Box
              sx={{
                fontFamily: row.display_name ? "inherit" : "monospace",
                fontSize: "0.85rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={row.display_name ? `${row.display_name} (ID: ${row.id})` : row.id}
            >
              {displayText}
            </Box>
          );
        },
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.8,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.value === "assessment" ? "Assessment" : "Roleplay"}
            size="small"
            color={params.value === "assessment" ? "primary" : "secondary"}
          />
        ),
      },
      {
        field: "resource_name",
        headerName: "Resource",
        flex: 2,
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ResLinkRow;
          const resourceId = row.resource_id || row.resource_key;

          return resourceId ? (
            <Button
              variant="text"
              size="small"
              onClick={() =>
                handleViewResource(row.type, resourceId, organizationId)
              }
              sx={{
                textTransform: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.value}
            </Button>
          ) : (
            <span>{params.value}</span>
          );
        },
      },
      {
        field: "agent_config_name",
        headerName: "Agent Config",
        flex: 2,
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ResLinkRow;

          return row.agent_config_id ? (
            <Button
              variant="text"
              size="small"
              onClick={() =>
                navigate(`/dashboard/agent-configs/${row.agent_config_id}`)
              }
              sx={{
                textTransform: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.value}
            </Button>
          ) : (
            <span>{params.value}</span>
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 1.5,
        minWidth: 180,
        headerAlign: "center",
        align: "center",
        valueGetter: (params: any) => {
          return params
            ? format(new Date(params), "yyyy-MM-dd HH:mm:ss")
            : null;
        },
      },
      {
        field: "mainLink",
        headerName: "Main Link",
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ResLinkRow;
          return (
            <IconButton
              size="small"
              onClick={() => handleCopyLink(`main-${row.id}`, row.mainLinkUrl)}
              title="Copy main link"
              color="primary"
            >
              {copiedLinkId === `main-${row.id}` ? (
                <CheckCircleIcon fontSize="small" color="success" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
      {
        field: "testLink",
        headerName: "Test Link",
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ResLinkRow;
          return (
            <IconButton
              size="small"
              onClick={() => handleCopyLink(`test-${row.id}`, row.testLinkUrl)}
              title="Copy test link"
              color="secondary"
            >
              {copiedLinkId === `test-${row.id}` ? (
                <CheckCircleIcon fontSize="small" color="success" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
    ],
    [copiedLinkId, organizationId, handleViewResource, navigate]
  );

  if (linksLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mt: 2,
          width: "100%",
        }}
      >
        {/* Search Query */}
        <TextField
          label="Search Links"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by Link ID, Resource, or Agent Config"
          sx={{ flex: 1 }}
        />

        {/* Type Filter */}
        <FormControl variant="outlined" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            label="Type"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="assessment">Assessment</MenuItem>
            <MenuItem value="roleplay">Roleplay</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={() => {
            setSearchQuery("");
            setTypeFilter("");
          }}
        >
          Reset Filters
        </Button>
      </Box>

      <Box sx={{ height: "50vh", width: "100%", mt: 2 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
        />
      </Box>
    </>
  );
};

export default ResLinksTab;
